import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// ─── Registries ───────────────────────────────────────────────────────
import { HOME_ARTICLES } from "../data/insights/insightsRegistry";
import { HOME_SERVICES } from "../data/services/servicesRegistry";
import { PRODUCTS_REGISTRY } from "../data/products/productsRegistry";
import { InView } from "../utils/Animation";

const STATS = [
  { value: "50+", label: "Projects Delivered" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "< 48h", label: "Avg. First Prototype" },
  { value: "5+", label: "Years of Excellence" },
];

// ─── Sections ─────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-itl-navy">
      <div className="absolute inset-0">
        <img
          src="/images/hero.jpg"
          alt="Hero"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/70 via-itl-navy/50 to-itl-navy/90" />
      </div>

      <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
        <div className="h-[40%] w-px bg-white/20" />
      </div>

      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12 pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-6"
        >
          Nigeria · Africa · Global
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.08] max-w-4xl mb-8"
        >
          World-class digital solutions.
          <br />
          <em className="italic">Built for Africa.</em>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="text-white/70 text-lg max-w-xl leading-relaxed mb-10"
        >
          Incubator Tech Limited engineers web applications, mobile products,
          and technology platforms that deliver results — in record time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="flex flex-wrap gap-4"
        >
          <Link
            to="/services"
            className="px-8 py-4 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-blue-600 transition-colors duration-200"
          >
            Explore Services
          </Link>
          <Link
            to="/products"
            className="px-8 py-4 border border-white/40 text-white text-[12px] font-semibold uppercase tracking-widest hover:border-white hover:bg-white/10 transition-colors duration-200"
          >
            View Our Work
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-white/30 text-[10px] uppercase tracking-[0.2em]">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="w-px h-8 bg-white/30"
        />
      </motion.div>
    </section>
  );
}

function StatsBar() {
  return (
    <section className="bg-itl-navy border-t border-white/10">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s, i) => (
            <InView key={s.label} delay={i * 0.1}>
              <div className="px-8 py-10 text-center">
                <p className="font-serif text-4xl font-light text-white mb-1">
                  {s.value}
                </p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">
                  {s.label}
                </p>
              </div>
            </InView>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemosSection() {
  return (
    <section className="py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <InView>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-itl-blue mb-3">
              Featured Work
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-itl-navy leading-tight max-w-lg">
              Products &amp; demos that speak for themselves.
            </h2>
          </InView>
          <InView delay={0.15}>
            <Link
              to="/products"
              className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-itl-blue hover:text-itl-navy transition-colors group"
            >
              View all work
              <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
                →
              </span>
            </Link>
          </InView>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-100">
          {/* Featured card */}
          <InView className="lg:row-span-1 bg-white group">
            <Link to={PRODUCTS_REGISTRY[0].href} className="block h-full">
              <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-80">
                <img
                  src={PRODUCTS_REGISTRY[0].image}
                  alt={PRODUCTS_REGISTRY[0].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-itl-navy/20 group-hover:bg-itl-navy/10 transition-colors duration-300" />
              </div>
              <div className="p-8 border-t border-gray-100">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-itl-blue mb-3">
                  {PRODUCTS_REGISTRY[0].tag}
                </p>
                <h3 className="font-serif text-2xl font-light text-itl-navy mb-3 group-hover:text-itl-blue transition-colors">
                  {PRODUCTS_REGISTRY[0].title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {PRODUCTS_REGISTRY[0].excerpt}
                </p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-2">
                  View Demo{" "}
                  <span className="group-hover:translate-x-1 transition-transform inline-block">
                    →
                  </span>
                </p>
              </div>
            </Link>
          </InView>

          {/* Side cards */}
          <div className="flex flex-col gap-px">
            {PRODUCTS_REGISTRY.slice(1).map((demo, i) => (
              <InView
                key={demo.title}
                delay={(i + 1) * 0.1}
                className="bg-white group"
              >
                <Link to={demo.href} className="flex h-full">
                  <div className="relative w-40 lg:w-48 shrink-0 overflow-hidden">
                    <img
                      src={demo.image}
                      alt={demo.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 flex flex-col justify-between">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-itl-blue mb-2">
                        {demo.tag}
                      </p>
                      <h3 className="font-serif text-lg font-light text-itl-navy mb-2 group-hover:text-itl-blue transition-colors leading-snug">
                        {demo.title}
                      </h3>
                      <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-2">
                        {demo.excerpt}
                      </p>
                    </div>
                    <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-1.5">
                      Explore{" "}
                      <span className="group-hover:translate-x-1 transition-transform inline-block">
                        →
                      </span>
                    </p>
                  </div>
                </Link>
              </InView>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="py-28 bg-[#f7f7f5]">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-16">
          <InView>
            <div className="lg:sticky lg:top-32">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-itl-blue mb-4">
                What We Do
              </p>
              <h2 className="font-serif text-4xl font-light text-itl-navy leading-snug mb-8">
                Let's turn your biggest opportunities into your next big moves.
              </h2>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-itl-navy border-b border-itl-navy hover:text-itl-blue hover:border-itl-blue pb-1 transition-colors group"
              >
                All services
                <span className="group-hover:translate-x-1 transition-transform inline-block">
                  →
                </span>
              </Link>
            </div>
          </InView>

          <div className="flex flex-col">
            {HOME_SERVICES.map((s, i) => (
              <InView key={s.number} delay={i * 0.08}>
                <Link
                  to={s.href}
                  className="group border-t border-gray-200 py-8 flex gap-8 items-start hover:bg-white -mx-4 px-4 transition-colors duration-200"
                >
                  <span className="text-[11px] font-mono text-gray-300 mt-1 shrink-0">
                    {s.number}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl font-light text-itl-navy mb-3 group-hover:text-itl-blue transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-gray-500 text-[14px] leading-relaxed max-w-lg">
                      {s.body}
                    </p>
                  </div>
                  <span className="text-itl-blue text-xl mt-1 group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </Link>
              </InView>
            ))}
            <div className="border-t border-gray-200" />
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyUsSection() {
  return (
    <section className="py-28 bg-itl-navy text-white overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <InView>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-itl-blue mb-5">
                The Incubator Difference
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl font-light leading-snug mb-8">
                World-class tech.{" "}
                <em className="italic">Delivered at African speed.</em>
              </h2>
              <p className="text-white/60 text-base leading-loose mb-8 max-w-lg">
                We combine globally battle-tested technology stacks — React,
                Node.js, MongoDB, React Native — with deep knowledge of the
                Nigerian and African market to build products that are
                resilient, scalable, and genuinely fit for purpose.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-white border-b border-white/30 hover:border-white pb-1 transition-colors group"
              >
                Learn about us
                <span className="group-hover:translate-x-1 transition-transform inline-block">
                  →
                </span>
              </Link>
            </InView>
          </div>

          <InView delay={0.2}>
            <div className="relative">
              <img
                src="/images/lp.jpg"
                alt="Our team at work"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-itl-blue pointer-events-none" />
              <div className="absolute -top-6 -right-6 w-32 h-32 border border-white/10 pointer-events-none" />
            </div>
          </InView>
        </div>
      </div>
    </section>
  );
}

function InsightsSection() {
  return (
    <section className="py-28 bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <InView>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-itl-blue mb-3">
              Insights &amp; Thinking
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-itl-navy leading-tight max-w-xl">
              Ideas that move African tech forward.
            </h2>
          </InView>
          <InView delay={0.15}>
            <Link
              to="/insights"
              className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-itl-blue hover:text-itl-navy transition-colors group shrink-0"
            >
              All articles
              <span className="group-hover:translate-x-1 transition-transform inline-block">
                →
              </span>
            </Link>
          </InView>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-100">
          {HOME_ARTICLES.map((article, i) => (
            <InView
              key={article.slug}
              delay={i * 0.07}
              className="bg-white group"
            >
              <Link to={article.href} className="block h-full">
                <div className="relative overflow-hidden aspect-[3/2]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-itl-blue">
                      {article.tag}
                    </span>
                    <span className="text-gray-300 text-xs">/</span>
                    <span className="text-gray-400 text-[11px]">
                      {article.date}
                    </span>
                  </div>
                  <h3 className="font-serif text-[17px] font-light text-itl-navy leading-snug mb-3 group-hover:text-itl-blue transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-500 text-[13px] leading-relaxed line-clamp-3">
                    {article.excerpt}
                  </p>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-1.5">
                    Read article
                    <span className="group-hover:translate-x-1 transition-transform inline-block">
                      →
                    </span>
                  </p>
                </div>
              </Link>
            </InView>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="py-32 bg-[#f7f7f5] relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span className="font-serif text-[15vw] font-bold text-gray-100 whitespace-nowrap leading-none">
          Let's Build
        </span>
      </div>

      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12 text-center">
        <InView>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-6">
            Ready to Start?
          </p>
          <h2 className="font-serif text-4xl lg:text-6xl font-light text-itl-navy leading-[1.1] mb-8 max-w-3xl mx-auto">
            Your next great product starts with a single conversation.
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto mb-10 leading-relaxed">
            Whether you have a full spec or just a raw idea, we can help. Tell
            us what you're building — we'll take it from there.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="px-10 py-4 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-itl-navy transition-colors duration-200"
            >
              Get In Touch
            </Link>
            <Link
              to="/products"
              className="px-10 py-4 border border-itl-navy text-itl-navy text-[12px] font-semibold uppercase tracking-widest hover:bg-itl-navy hover:text-white transition-colors duration-200"
            >
              Browse Our Products
            </Link>
          </div>
        </InView>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <HeroSection />
      <StatsBar />
      <DemosSection />
      <ServicesSection />
      <WhyUsSection />
      <InsightsSection />
      <CtaSection />
    </>
  );
}
